import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { ShoppingBag, Check, Heart, Flag, ShieldAlert, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import ProductReviews from "@/components/ProductReviews";
import TrustBadge from "@/components/TrustBadge";
import ReportSellerModal from "@/components/ReportSellerModal";
import NegotiationModal from "@/components/NegotiationModal";
import ProductGallery from "@/components/ProductGallery";
import ImageLightbox from "@/components/ImageLightbox";
import { fbViewContent } from "@/lib/pixel";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { add } = useCart();
  const wishlist = useWishlist();
  const [p, setP] = useState(null);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [reporting, setReporting] = useState(false);
  const [bargaining, setBargaining] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => {
      setP(data);
      setSize(data.sizes?.[0] || null);
      setColor(data.colors?.[0] || null);
      fbViewContent(data);
    }).catch((err) => {
      console.warn("Product not found", err);
      navigate("/shop");
    });
  }, [id, navigate]);

  if (!p) return <div className="p-12 font-mono-b" data-testid="pd-loading">LOADING...</div>;

  const soldOut = (p.stock ?? 0) <= 0;

  const handleAdd = () => {
    if (soldOut) { toast.error("Sold out"); return; }
    if (p.sizes?.length && !size) { toast.error("Pick a size"); return; }
    // Enforce stock at click time
    const inCart = 0; // start fresh check
    add(p, { size, color, quantity: 1 });
    toast.success("Added to bag", { description: p.name });
  };

  const handleBuyNow = () => {
    if (soldOut) { toast.error("Sold out"); return; }
    if (p.sizes?.length && !size) { toast.error("Pick a size"); return; }
    add(p, { size, color, quantity: 1 });
    navigate("/checkout");
  };

  const wished = wishlist?.has ? wishlist.has(p.product_id) : false;

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10" data-testid="product-detail">
      <div className="grid md:grid-cols-2 gap-10">
        <div>
          <ProductGallery
            images={p.images || []}
            alt={p.name}
            soldOut={soldOut}
            onImageClick={(i) => setLightboxIdx(i)}
            overlay={
              <>
                {!p.is_custom && !soldOut && <span className="sticker orange absolute top-4 left-4 rotate-[-4deg] z-10">THRIFTED</span>}
                {p.tags?.includes("1 of 1") && !soldOut && <span className="sticker absolute top-4 right-4 rotate-[3deg] z-10">1 OF 1</span>}
                {soldOut && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10" data-testid="pd-sold-stamp">
                    <div className="bg-black text-[#FF007F] font-display text-7xl md:text-8xl px-8 py-3 border-4 border-[#FF007F] rotate-[-12deg] tracking-wider shadow-[8px_8px_0_0_#FF007F]">
                      SOLD
                    </div>
                  </div>
                )}
              </>
            }
          />
        </div>
        <div>
          <p className="font-mono-b text-xs uppercase text-black/60">{p.category.replace("-", " ")}</p>
          <h1 className="font-display text-5xl md:text-6xl leading-tight mt-1" data-testid="pd-title">{p.name}</h1>
          <p className="font-mono-b text-3xl mt-4 font-bold" data-testid="pd-price">₹{p.price}</p>
          {p.tags?.length > 0 && (
            <div className="flex gap-2 mt-4 flex-wrap">
              {p.tags.map((t) => <span key={t} className="sticker text-[10px]">{t.toUpperCase()}</span>)}
            </div>
          )}
          <p className="font-mono-b mt-6 text-black/80" data-testid="pd-desc">{p.description}</p>

          {p.sizes?.length > 0 && (
            <div className="mt-6" data-testid="pd-size-picker">
              <p className="font-mono-b font-bold uppercase text-sm mb-2">SIZE:</p>
              <div className="flex gap-2 flex-wrap">
                {p.sizes.map((s) => (
                  <button key={s} data-testid={`pd-size-${s}`} onClick={() => setSize(s)} className={`px-4 py-2 border-2 border-black font-mono-b font-bold hover-nudge ${size === s ? "bg-black text-[#D4FF00]" : "bg-white"}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {p.colors?.length > 0 && (
            <div className="mt-4" data-testid="pd-color-picker">
              <p className="font-mono-b font-bold uppercase text-sm mb-2">COLOR:</p>
              <div className="flex gap-2 flex-wrap">
                {p.colors.map((c) => (
                  <button key={c} data-testid={`pd-color-${c}`} onClick={() => setColor(c)} className={`px-4 py-2 border-2 border-black font-mono-b font-bold hover-nudge ${color === c ? "bg-black text-[#D4FF00]" : "bg-white"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 flex gap-3 flex-wrap">
            <button onClick={handleAdd} disabled={soldOut} data-testid="pd-add-to-cart" className="flex-1 bg-white border-2 border-black brutal-shadow hover-nudge px-6 py-4 font-mono-b font-bold uppercase flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
              <ShoppingBag className="w-4 h-4" /> {soldOut ? "SOLD OUT" : "Add to bag"}
            </button>
            <button onClick={handleBuyNow} disabled={soldOut} data-testid="pd-buy-now" className="flex-1 bg-black text-[#D4FF00] border-2 border-black brutal-shadow hover-nudge px-6 py-4 font-mono-b font-bold uppercase disabled:opacity-40 disabled:cursor-not-allowed">
              {soldOut ? "GONE" : "Buy now"}
            </button>
            {!p.is_custom && p.seller_id && p.bargain_enabled !== false && !soldOut && user && user.user_id !== p.seller_id && (
              <button
                onClick={() => setBargaining(true)}
                data-testid="pd-bargain"
                className="flex-1 bg-[#D4FF00] border-2 border-black brutal-shadow hover-nudge px-6 py-4 font-mono-b font-bold uppercase inline-flex items-center justify-center gap-2"
              >
                <MessageSquare className="w-4 h-4" /> BARGAIN
              </button>
            )}
            <button
              onClick={() => wishlist?.toggle(p)}
              data-testid="pd-wishlist"
              aria-label={wished ? "Remove from wishlist" : "Save to wishlist"}
              className={`border-2 border-black brutal-shadow hover-nudge px-4 py-4 font-mono-b font-bold uppercase flex items-center gap-2 ${wished ? "bg-[#FF007F] text-white" : "bg-white text-black"}`}
            >
              <Heart className={`w-4 h-4 ${wished ? "fill-current" : ""}`} /> {wished ? "SAVED" : "SAVE"}
            </button>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-3 text-sm font-mono-b">
            <div className="border-2 border-black p-3 bg-white"><Check className="inline w-4 h-4 text-[#FF007F]" /> Flat ₹99 shipping · India</div>
            <div className="border-2 border-black p-3 bg-white">
              <Check className="inline w-4 h-4 text-[#FF007F]" /> {p.seller_id ? "No returns · marketplace" : "7-day returns"}
            </div>
          </div>

          {/* Seller trust panel */}
          <div className="mt-6 border-2 border-black bg-[#F4F4F0] p-4" data-testid="pd-seller-panel">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <p className="font-mono-b text-[10px] uppercase text-black/60">Sold by</p>
                <p className="font-display text-xl">{p.seller_name || "OUTLAW-D"}</p>
              </div>
              <TrustBadge badges={p.seller_id ? (p.seller_badges || []) : ["verified"]} size="md" />
            </div>
            {p.seller_id && (
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <p className="font-mono-b text-xs text-black/60">{p.seller_sales_count || 0} sales</p>
                {user && user.user_id !== p.seller_id && (
                  <button
                    onClick={() => setReporting(true)}
                    data-testid="pd-report-seller"
                    className="ml-auto inline-flex items-center gap-1 border-2 border-black bg-white px-2 py-1 text-[11px] font-mono-b font-bold uppercase hover-nudge"
                  >
                    <Flag className="w-3 h-3" /> REPORT SELLER
                  </button>
                )}
              </div>
            )}
            {!p.seller_id && (
              <p className="font-mono-b text-[11px] mt-2 text-black/60"><ShieldAlert className="inline w-3 h-3" /> Sold directly by OUTLAW-D · Returns available.</p>
            )}
          </div>
        </div>
      </div>
      <ProductReviews productId={p.product_id} />
      {reporting && (
        <ReportSellerModal
          sellerId={p.seller_id}
          sellerName={p.seller_name}
          onClose={() => setReporting(false)}
        />
      )}
      {bargaining && (
        <NegotiationModal
          productId={p.product_id}
          onClose={() => setBargaining(false)}
        />
      )}
      {lightboxIdx !== null && (
        <ImageLightbox
          images={p.images || []}
          startIndex={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </div>
  );
}
